import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { resolveGoogleMapTypeId } from './mapViewUtils';

/**
 * Sync map type from map-layer preference.
 * Must be rendered as a child of <Map /> so useMap() can resolve.
 */
export function SyncMapType({ mapLayer }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    map.setMapTypeId(resolveGoogleMapTypeId(mapLayer));
  }, [map, mapLayer]);

  return null;
}

/**
 * Sync colorScheme dynamically so the Map never needs to remount for
 * dark/light theme changes.  Remounting resets zoom and pan position.
 * Must be rendered as a child of <Map />.
 */
export function SyncColorScheme({ colorScheme }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !colorScheme) return;
    map.setOptions({ colorScheme });
  }, [map, colorScheme]);

  return null;
}

/**
 * Reports when the map is visually ready (tiles loaded / first idle).
 * This gives users a more truthful loading state than simply seeing a map instance.
 */
export function MapLoadReporter({ onMapReady, searchKey = 0 }) {
  const map = useMap();

  useEffect(() => {
    if (!map || typeof onMapReady !== 'function') return;

    let reported = false;
    const report = () => {
      if (reported) return;
      reported = true;
      onMapReady();
    };

    const idleListener = map.addListener('idle', report);
    const tilesListener = map.addListener('tilesloaded', report);
    const fallbackTimer = window.setTimeout(report, 2000);

    return () => {
      window.google?.maps?.event?.removeListener?.(idleListener);
      window.google?.maps?.event?.removeListener?.(tilesListener);
      window.clearTimeout(fallbackTimer);
    };
  // searchKey increments on each new search, re-arming the reported flag and fallback timer.
  }, [map, onMapReady, searchKey]);

  return null;
}

/**
 * Reports taps on the map (lat, lng). Must be rendered as a child of <Map />.
 */
export function MapClickHandler({ onMapClick }) {
  const map = useMap();
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;

  useEffect(() => {
    if (!map) return;
    const clickListener = map.addListener('click', (event) => {
      const lat = event?.latLng?.lat?.();
      const lng = event?.latLng?.lng?.();
      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      onMapClickRef.current?.(lat, lng);
    });
    return () => {
      window.google?.maps?.event?.removeListener?.(clickListener);
    };
  }, [map]);

  return null;
}
